import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, MapPin, Filter, Star, X, 
  Pizza, Sandwich, Fish, Drumstick, Leaf, 
  Beef, CookingPot 
} from 'lucide-react';
import '../styles/restaurants.css';

const Restaurants = () => {
  const [sortBy, setSortBy] = useState('rating');
  const [currentView, setCurrentView] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
  const [selectedDietaryRestrictions, setSelectedDietaryRestrictions] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);

  const cuisineIcons = {
    'Middle Eastern': Sandwich,
    'Japanese': Fish,
    'Italian': Pizza,
    'American': Sandwich,
    'Mexican': Beef,
    'Indian': CookingPot,
    'French': Leaf,
    'Vietnamese': Leaf,
    'Vegetarian': Leaf,
    'Turkish': Drumstick,
    'Pakistani': Beef
  };
  
  // Memoize the restaurants array
  const restaurants = useMemo(() => [
    {
      id: 1,
      name: "Al-Madina Grill",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "Middle Eastern",
      priceRange: "$$",
      location: "New York, NY",
      rating: 4.5,
      reviews: 128,
      description: "Authentic Middle Eastern cuisine in a warm atmosphere",
      tags: ["Halal", "Grill", "Family Friendly"]
    },
    {
      id: 2,
      name: "Sushi Palace",
      image: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "Japanese",
      priceRange: "$$$",
      location: "Los Angeles, CA",
      rating: 4.7,
      reviews: 245,
      description: "Fresh sushi and sashimi in a modern setting",
      tags: ["Sushi", "Seafood", "Date Night"]
    },
    {
      id: 3,
      name: "La Pizzeria",
      image: "https://images.unsplash.com/photo-1590947132387-155cc02f3212?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "Italian",
      priceRange: "$$",
      location: "Chicago, IL",
      rating: 4.3,
      reviews: 98,
      description: "Wood-fired pizzas with authentic Italian flavors",
      tags: ["Pizza", "Casual Dining", "Family Friendly"]
    },
    {
      id: 4,
      name: "The Burger Joint",
      image: "https://images.unsplash.com/photo-1572802419224-296b0aeee0d9?q=80&w=2015&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "American",
      priceRange: "$",
      location: "Austin, TX",
      rating: 4.2,
      reviews: 87,
      description: "Classic burgers and fries in a retro diner setting",
      tags: ["Burgers", "Fast Food", "Casual"]
    },
    {
      id: 5,
      name: "Taco Fiesta",
      image: "https://images.unsplash.com/photo-1615870216519-2f9fa575fa5c?q=80&w=1932&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "Mexican",
      priceRange: "$$",
      location: "San Diego, CA",
      rating: 4.6,
      reviews: 156,
      description: "Vibrant Mexican street food with bold flavors",
      tags: ["Tacos", "Vegetarian Options", "Casual Dining"]
    },
    {
      id: 6,
      name: "Curry House",
      image: "https://images.unsplash.com/photo-1601050690597-df0568f70950?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "Indian",
      priceRange: "$$",
      location: "Houston, TX",
      rating: 4.4,
      reviews: 112,
      description: "Rich and flavorful Indian curries and biryanis",
      tags: ["Spicy", "Vegetarian Friendly", "Family Friendly"]
    },
    {
      id: 7,
      name: "Le Bistro",
      image: "https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTl8fHJlc3RhdXJhbnR8ZW58MHx8MHx8fDA%3D",
      cuisine: "French",
      priceRange: "$$$",
      location: "San Francisco, CA",
      rating: 4.8,
      reviews: 201,
      description: "Elegant French cuisine with a modern twist",
      tags: ["Fine Dining", "Romantic", "Wine Pairing"]
    },
    {
      id: 8,
      name: "Pho Street",
      image: "https://images.unsplash.com/photo-1563245372-f21724e3856d?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "Vietnamese",
      priceRange: "$",
      location: "Seattle, WA",
      rating: 4.1,
      reviews: 76,
      description: "Hearty and comforting Vietnamese pho and noodles",
      tags: ["Noodles", "Quick Bite", "Vegetarian Options"]
    },
    {
      id: 9,
      name: "The Steakhouse",
      image: "https://images.unsplash.com/photo-1600891964092-4316c288032e?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "American",
      priceRange: "$$$$",
      location: "Dallas, TX",
      rating: 4.9,
      reviews: 312,
      description: "Premium cuts of steak in a luxurious setting",
      tags: ["Steak", "Fine Dining", "Wine Pairing"]
    },
    {
      id: 10,
      name: "Green Garden",
      image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      cuisine: "Vegetarian",
      priceRange: "$$",
      location: "Portland, OR",
      rating: 4.0,
      reviews: 89,
      description: "Fresh and healthy vegetarian dishes",
      tags: ["Vegetarian", "Vegan Options", "Healthy"]
    },
    {
      id: 11,
      name: "Istanbul Kitchen",
      image: "https://plus.unsplash.com/premium_photo-1686090448301-4c453ee74718?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8cmVzdGF1cmFudHxlbnwwfHwwfHx8MA%3D%3D",
      cuisine: "Turkish",
      priceRange: "$$$",
      location: "Brooklyn, NY",
      rating: 4.7,
      reviews: 156,
      description: "Traditional Turkish dishes with a modern twist",
      tags: ["Halal", "Turkish", "Fine Dining"]
    },
    {
      id: 12,
      name: "Kabab Express",
      image: "https://images.unsplash.com/photo-1537047902294-62a40c20a6ae?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8cmVzdGF1cmFudHxlbnwwfHwwfHx8MA%3D%3D",
      cuisine: "Pakistani",
      priceRange: "$",
      location: "Queens, NY",
      rating: 4.3,
      reviews: 92,
      description: "Quick and delicious Pakistani street food",
      tags: ["Halal", "Fast Food", "Late Night"]
    }
  ], []); // Empty dependency array means this will only be created once

  const [filteredRestaurants, setFilteredRestaurants] = useState(restaurants);

  // Memoize cuisines and priceRanges
  const cuisines = useMemo(() => 
    [...new Set(restaurants.map(restaurant => restaurant.cuisine))], 
    [restaurants]
  );

  const priceRanges = useMemo(() => 
    [...new Set(restaurants.map(restaurant => restaurant.priceRange))], 
    [restaurants]
  );

  const applyFilters = useCallback(() => {
    let results = [...restaurants];

    if (searchQuery) {
      results = results.filter(restaurant =>
        restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        restaurant.cuisine.toLowerCase().includes(searchQuery.toLowerCase()) ||
        restaurant.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (locationQuery) {
      results = results.filter(restaurant =>
        restaurant.location.toLowerCase().includes(locationQuery.toLowerCase())
      );
    }

    if (selectedCuisines.length > 0) {
      results = results.filter(restaurant => selectedCuisines.includes(restaurant.cuisine));
    }

    if (selectedPriceRanges.length > 0) {
      results = results.filter(restaurant => selectedPriceRanges.includes(restaurant.priceRange));
    }

    // Additional filtering for dietary restrictions and amenities
    if (selectedDietaryRestrictions.length > 0) {
      results = results.filter(restaurant => 
        selectedDietaryRestrictions.some(restriction => 
          restaurant.tags.includes(restriction)
        )
      );
    }

    if (selectedAmenities.length > 0) {
      results = results.filter(restaurant => 
        selectedAmenities.some(amenity => 
          restaurant.tags.includes(amenity)
        )
      );
    }

    switch (sortBy) {
      case 'rating':
        results.sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        results.sort((a, b) => b.reviews - a.reviews);
        break;
      case 'pricelow':
        results.sort((a, b) => a.priceRange.length - b.priceRange.length);
        break;
      case 'pricehigh':
        results.sort((a, b) => b.priceRange.length - a.priceRange.length);
        break;
      default:
        break;
    }

    setFilteredRestaurants(results);
  }, [
    restaurants, 
    searchQuery, 
    locationQuery, 
    sortBy, 
    selectedCuisines, 
    selectedPriceRanges, 
    selectedDietaryRestrictions, 
    selectedAmenities
  ]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterReset = () => {
    setSelectedCuisines([]);
    setSelectedPriceRanges([]);
    setSelectedDietaryRestrictions([]);
    setSelectedAmenities([]);
  };

  return (
    <div className="restaurants-container">
      <div className="search-bar">
        <div className="search-inputs">
          <div className="input-group">
            <Search className="icon" size={20} />
            <input
              type="text"
              placeholder="Cuisine, Restaurant name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="input-group">
            <MapPin className="icon" size={20} />
            <input
              type="text"
              placeholder="Near me, Location, address..."
              value={locationQuery}
              onChange={(e) => setLocationQuery(e.target.value)}
            />
          </div>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="rating">Highest Rated</option>
            <option value="reviews">Most Reviewed</option>
            <option value="pricelow">Price: Low to High</option>
            <option value="pricehigh">Price: High to Low</option>
          </select>

          <button className="filter-btn" onClick={() => setShowFilters(true)}>
            <Filter size={20} />
            Filters
          </button>

          <div className="view-toggle">
            <button
              className={currentView === 'grid' ? 'active' : ''}
              onClick={() => setCurrentView('grid')}
            >
              Grid
            </button>
            
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="filter-sidebar">
          <div className="filter-sidebar-header">
            <h3>
              <Filter size={24} /> Filter Restaurants
            </h3>
            <button className="filter-close-btn" onClick={() => setShowFilters(false)}>
              <X size={24} />
            </button>
          </div>

          <div className="filter-section">
            <h5>Cuisine</h5>
            {cuisines.map(cuisine => {
              // Get the icon component for the current cuisine
              const IconComponent = cuisineIcons[cuisine] || Sandwich;
              
              return (
                <label key={cuisine} className="cuisine-filter-label">
                  <div className="cuisine-filter-content">
                    <IconComponent size={20} />
                    <span>{cuisine}</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedCuisines.includes(cuisine)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCuisines([...selectedCuisines, cuisine]);
                      } else {
                        setSelectedCuisines(selectedCuisines.filter(c => c !== cuisine));
                      }
                    }}
                  />
                </label>
              );
            })}
          </div>

          <div className="filter-section">
            <h5>Price Range</h5>
            {priceRanges.map(range => (
              <label key={range}>
                <input
                  type="checkbox"
                  checked={selectedPriceRanges.includes(range)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPriceRanges([...selectedPriceRanges, range]);
                    } else {
                      setSelectedPriceRanges(selectedPriceRanges.filter(r => r !== range));
                    }
                  }}
                />
                {range}
              </label>
            ))}
          </div>

          <div className="filter-section">
            <h5>Dietary Restrictions</h5>
            <label>
              <input
                type="checkbox"
                checked={selectedDietaryRestrictions.includes('Vegetarian')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDietaryRestrictions([...selectedDietaryRestrictions, 'Vegetarian']);
                  } else {
                    setSelectedDietaryRestrictions(selectedDietaryRestrictions.filter(r => r !== 'Vegetarian'));
                  }
                }}
              />
              Vegetarian
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedDietaryRestrictions.includes('Vegan')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDietaryRestrictions([...selectedDietaryRestrictions, 'Vegan']);
                  } else {
                    setSelectedDietaryRestrictions(selectedDietaryRestrictions.filter(r => r !== 'Vegan'));
                  }
                }}
              />
              Vegan
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedDietaryRestrictions.includes('Gluten-Free')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDietaryRestrictions([...selectedDietaryRestrictions, 'Gluten-Free']);
                  } else {
                    setSelectedDietaryRestrictions(selectedDietaryRestrictions.filter(r => r !== 'Gluten-Free'));
                  }
                }}
              />
              Gluten-Free
            </label>
          </div>

          <div className="filter-section">
            <h5>Amenities</h5>
            <label>
              <input
                type="checkbox"
                checked={selectedAmenities.includes('Outdoor Seating')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedAmenities([...selectedAmenities, 'Outdoor Seating']);
                  } else {
                    setSelectedAmenities(selectedAmenities.filter(a => a !== 'Outdoor Seating'));
                  }
                }}
              />
              Outdoor Seating
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedAmenities.includes('Delivery')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedAmenities([...selectedAmenities, 'Delivery']);
                  } else {
                    setSelectedAmenities(selectedAmenities.filter(a => a !== 'Delivery'));
                  }
                }}
              />
              Delivery
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedAmenities.includes('Takeout')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedAmenities([...selectedAmenities, 'Takeout']);
                  } else {
                    setSelectedAmenities(selectedAmenities.filter(a => a !== 'Takeout'));
                  }
                }}
              />
              Takeout
            </label>
          </div>

          <div className="filter-actions">
            <button 
              className="reset-btn" 
              onClick={handleFilterReset}
            >
              Reset
            </button>
            <button 
              className="apply-btn" 
              onClick={() => setShowFilters(false)}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      <div className={`restaurant-${currentView}`}>
        {filteredRestaurants.map(restaurant => (
          <div key={restaurant.id} className="restaurant-card">
            <img src={restaurant.image} alt={restaurant.name} />

            <div className="restaurant-info1" >
              <div className="header">
                <h3>{restaurant.name}</h3>
                <span className="rating">
                  {restaurant.rating} <Star size={16} className="star" />
                </span>
              </div>

              <div className="details">
                <p>{restaurant.cuisine} • {restaurant.priceRange}</p>
                <p className="location">
                  <MapPin size={16} />
                  {restaurant.location}
                </p>
              </div>

              <p className="description">{restaurant.description}</p>

              <div className="tags">
                {restaurant.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>

              <div className="card-footer">
                <span>{restaurant.reviews} reviews</span>
                <Link to={`/restaurants/${restaurant.id}`} className="view-details">
                  View Details
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Restaurants;